import React, { useState, useEffect } from 'react'
import { useNoticias } from '../../hooks/useNoticias'
import { useNoticiasSearch } from '../../hooks/useNoticiasSearch'
import { useNavigate } from 'react-router-dom'
import { SearchBar, Pagination, LoadingSpinner } from '../utils'
import { buildFileUrl } from '../../config/strapi'

export function NoticiasPage() {
  const { data: noticias, loading, error, refetch } = useNoticias()
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)
  
  const {
    searchTerm,
    setSearchTerm,
    filteredNoticias,
    allFilteredNoticias,
    pagination,
    clearSearch,
    hasActiveFilters
  } = useNoticiasSearch({
    noticias: noticias?.list_noticias || [],
    itemsPerPage: 6 // 6 notícias por página
  })

  // Hook para detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleNoticiaClick = (noticia: any) => {
    // Navega diretamente para a notícia pelo ID
    // Reason: Passa a origem 'noticias' para que o botão "Voltar" saiba para onde retornar
    navigate(`/noticia/${noticia.id}`, { state: { from: 'noticias' } })
  }

  if (loading) {
    return <LoadingSpinner message="Carregando notícias..." />
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        background: 'white',
        color: '#333'
      }}>
        <h2>Erro ao carregar notícias</h2>
        <p>{error}</p>
        <button
          onClick={refetch}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'white',
      color: '#333',
      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      letterSpacing: 0.5
    }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '16px 20px',
        width: '100%',
        boxSizing: 'border-box'
      }}>

        {/* Campo de Busca */}
        <SearchBar
          placeholder="Buscar notícias por título, descrição, tipo ou autor..."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={clearSearch}
          showResultsCount={hasActiveFilters}
          resultsCount={allFilteredNoticias.length}
          resultsLabel={allFilteredNoticias.length === 1 ? 'notícia encontrada' : 'notícias encontradas'}
        />
        
        {/* Lista de notícias */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
          maxWidth: 1000,
          margin: '0 auto',
          width: '100%'
        }}>
          {filteredNoticias.length > 0 ? (
            filteredNoticias.map((noticia: any, index: number) => (
              <div key={noticia.id}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isSmallMobile ? '1rem' : isMobile ? '1.5rem' : '2.5rem',
                  padding: isSmallMobile ? '1rem 0' : isMobile ? '1.5rem 0' : '2.5rem 0',
                  alignItems: 'flex-start'
                }}>
                  
                  {/* Imagem da notícia - responsiva */}
                  <div style={{
                    flexShrink: 0,
                    width: isMobile ? '100%' : '35%',
                    height: isMobile ? (isSmallMobile ? '150px' : '200px') : '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    order: isMobile ? -1 : 0
                  }}>
                    <img 
                      src={buildFileUrl(noticia.image?.url)}
                      alt={noticia.image?.alternativeText || 'Imagem da notícia'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>

                  {/* Conteúdo da notícia */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    textAlign: isMobile ? 'center' : 'left',
                    padding: isSmallMobile ? '0.25rem 0' : isMobile ? '0.5rem 0' : '1rem',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleNoticiaClick(noticia)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 114, 187, 0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}>
                    {/* Tipo/Categoria */}
                    <span style={{
                      color: '#0072BB',
                      borderRadius: '20px',
                      fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                      fontWeight: 500,
                      alignSelf: isMobile ? 'center' : 'flex-start'
                    }}>
                      {noticia.header?.type}
                    </span>

                    {/* Título - responsivo */}
                    <h3 style={{
                      fontSize: isSmallMobile ? '1.2rem' : isMobile ? '1.4rem' : '1.6rem',
                      fontWeight: 600,
                      color: '#0072BB',
                      lineHeight: 1.3,
                      margin: isSmallMobile ? '0.25rem 0' : isMobile ? '0.5rem 0' : '0.75rem 0',
                      textAlign: isMobile ? 'center' : 'left'
                    }}>
                      {noticia.header?.title}
                    </h3>
                    
                    {/* Descrição */}
                    <p style={{
                      fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                      lineHeight: isSmallMobile ? 1.5 : 1.6,
                      color: '#666',
                      margin: 0,
                      textAlign: isMobile ? 'center' : 'left'
                    }}>
                      {noticia.header?.description}
                    </p>
                  </div>
                </div>
                
                {/* Separador entre notícias */}
                {index < filteredNoticias.length - 1 && (
                  <div style={{
                    height: '1px',
                    background: '#e0e0e0',
                    margin: '0'
                  }} />
                )}
              </div>
            ))
          ) : (
            <div style={{
              textAlign: 'center',
              padding: isSmallMobile ? '1.5rem 1rem' : isMobile ? '2rem 1rem' : '3rem 1rem',
              color: '#666',
              fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem'
            }}>
              {hasActiveFilters ? (
                <>
                  <p>Nenhuma notícia encontrada para "{searchTerm}"</p>
                  <button
                    onClick={clearSearch}
                    style={{
                      marginTop: '1rem',
                      padding: isSmallMobile ? '8px 16px' : '10px 20px',
                      backgroundColor: '#0072BB',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: isSmallMobile ? '0.9rem' : '1rem'
                    }}
                  >
                    Limpar busca
                  </button>
                </>
              ) : (
                <p>Nenhuma notícia disponível</p>
              )}
            </div>
          )}
        </div>

        {/* Paginação */}
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={pagination.goToPage}
          totalItems={allFilteredNoticias.length}
          itemsPerPage={pagination.itemsPerPage}
        />
      </div>
    </div>
  )
} 